import { useEffect, useState } from 'react';
import { Divider, Typography } from 'ui';
import cn from 'classnames';
import { ModelDetailType } from 'src/modules/newCars/types';
import { PriceBreakuptype } from 'src/modules/newCars/types/model';
import { processModelSeoData } from 'src/modules/newCars/utils/seoContent';
import { Color, TypeScales } from 'ui/primitives';
import { EmiOffer } from '../../../EmiOffer/EmiOffer';
import { LatestNews } from '../../../LatestNews/LatestNews';
import VariantsList from '../../../VariantsList';
import VideoReview from '../../../VideoReview';
import Faq from '../../../content/Faq';
import ScrollableJumbo from '../../../content/ScrollableJumbo';
import { CityVariantSelector } from '../CityVariantSelector/CityVariantSelector';
import { UnderPriceCars } from '../UnderPriceCars/UnderPriceCars';
import UserReview from '../UserReview';
import Jumbo from '../../../content/Jumbo';
import TestDrivePopup from '../../../popups/TestDrivePopup';
import s from './Price.module.scss';

export const Price = ({
  content,
  strapiContent,
  priceBreakup,
  cookies,
}: {
  content: ModelDetailType;
  strapiContent: any;
  cookies: any;
  priceBreakup: PriceBreakuptype;
}) => {
  const [selectedCity, setSelectedCity] = useState<any>({
    name: cookies?.cityName || 'New Delhi',
    id: cookies?.cityId || 4,
  });
  const [selectedVariant, setSelectedVariant] = useState(
    content?.variants?.[0]
  );

  const [priceData, setPriceData] = useState<any>(priceBreakup);
  const [showTestDrive,setShowTestDrive] = useState(false);

  const headerPriceData = {
    heading: `On Road Price of <brand-name> <model-name> in ${cookies?.cityName}`,
    description:
      'Discover the on-road price of <brand-name> <model-name> in India, starting at Rs. <base-variant-price>. Choose from a range of variants, including the affordable <brand-name> <model-name> <base-variant-name> and the top-of-the-line <brand-name> <model-name> <top-variant-name>, priced at Rs. <top-variant-price>. For the best deals, visit your nearest <brand-name> <model-name> showroom in India. Explore our extensive lineup of <brand-name> cars and find the perfect fit for your needs. From stylish sedans to versatile SUVs, <brand-name> offers a diverse range of vehicles to suit every preference. Experience the excellence of <brand-name> craftsmanship and book a test drive today. Unlock a world of possibilities with <brand-name> <model-name> in India.',
  };

  useEffect(() => {
    if (!cookies.cookieExists) {
      document.cookie = `cityId=${cookies?.cityId}`;
      document.cookie = `cityName=${cookies?.cityName}`;
    }
  }, []);

  return (
    <div className={s.root}>
      <div className={s.header_data}>
        <Typography
          label={processModelSeoData(headerPriceData.heading, content)}
          variant={TypeScales.h1}
          renderAs="H1"
          className={s.heading}
        />
        <Typography
          label={
            <Jumbo
              jumbo={processModelSeoData(headerPriceData.description, content)
                
              }
              maxHeight={30}
            />
          }
          variant={TypeScales.base_big_r}
          color={Color.text_subheading}
        />
      </div>

      <div className={s.price_box}>
        <CityVariantSelector
          content={content}
          strapiContent={strapiContent}
          selectedCity={selectedCity}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          setSelectedCity={setSelectedCity}
          setPriceData={setPriceData}
        />
        <div className={s.box}>
          <div className={s.header}>
            <Typography
              label={`${content.hero?.brandName} ${content?.hero?.modelName} ${selectedVariant?.variantName}`}
              variant={TypeScales.body_para_m}
            />
          </div>
          <div className={s.data}>
            <div className={s.breakup}>
              <div className={s.item}>
                <Typography
                  label="Ex-Showroom Price"
                  variant={TypeScales.body_m}
                />
                <Typography
                  label={`₹ ${priceData?.exShowRoomPrice ?? 'N/A'}`}
                  color={Color.text_subheading}
                />
              </div>
              <div className={s.item}>
                <Typography
                  label="Individual Registration"
                  variant={TypeScales.body_m}
                />
                <Typography
                  label={`₹ ${priceData?.rto ?? 'N/A'}`}
                  color={Color.text_subheading}
                />
              </div>
              <div className={s.item}>
                <Typography label="Insurance" variant={TypeScales.body_m} />
                <Typography
                  label={`₹ ${priceData?.insurance ?? 'N/A'}`}
                  color={Color.text_subheading}
                />
              </div>
              {priceData?.otherCharges && (
                <div className={s.item}>
                  <Typography
                    label="Other charges"
                    variant={TypeScales.body_m}
                  />
                  <Typography
                    label={`₹ ${priceData?.otherCharges ?? 'N/A'}`}
                    color={Color.text_subheading}
                  />
                </div>
              )}
              <div className={cn('cmn_only_desktop', s.divider)}>
                <Divider />
              </div>
              <div className={cn(s.item, s.onRoad)}>
                <Typography
                  label="Total On Road Price"
                  variant={TypeScales.body_m}
                />
                <Typography
                  label={`₹ ${priceData?.onRoadPrice || 'N/A'}*`}
                  color={Color.text_default}
                />
              </div>
            </div>
            <EmiOffer price={selectedVariant?.priceInNumbers} handleGetEmiOffers={()=>setShowTestDrive(true)}/>
          </div>
        </div>
      </div>
      {/* {strapiContent?.carListing?.jumbo && (
        <Jumbo
          jumbo={processModelSeoData(strapiContent?.carListing?.jumbo, content)}
        />
      )} */}
      <div className={s.variant_list}>
        <VariantsList
          variants={content?.variants}
          modelName={content?.hero?.modelName}
          modelImage={content?.hero?.image}
          modelSlug={content?.modelSeoData?.modelSlug}
          brandSlug={content?.modelSeoData?.brandSlug}
        />
      </div>
      {content && <UnderPriceCars content={content} />}
      {strapiContent?.news?.length && (
        <LatestNews
          data={strapiContent.news}
          heading={`${content?.hero?.brandName} ${content?.hero?.modelName} latest news`}
        />
      )}
      {strapiContent?.calculatedReview && (
        <UserReview
          link={`/new-cars/${content?.modelSeoData?.brandSlug}-car/${content?.modelSeoData?.modelSlug}/review`}
          ratingData={strapiContent.userReview}
          calculatedReview={strapiContent.calculatedReview}
        />
      )}
      {strapiContent?.videoReview && (
        <VideoReview data={strapiContent.videoReview} />
      )}
      {strapiContent?.carListing?.mustRead && (
        <ScrollableJumbo
          heading="Must read article"
          description={strapiContent?.carListing?.mustRead}
        />
      )}
      {/* <BrandAd /> */}
      {strapiContent?.carListing?.faqTitle &&
        strapiContent?.carListing?.faqContent && (
          <Faq
            faqTitle={processModelSeoData(
              strapiContent?.carListing?.faqTitle,
              content
            )}
            faqContent={processModelSeoData(
              strapiContent?.carListing?.faqContent,
              content
            )}
          />
        )}
        {showTestDrive && <TestDrivePopup modelId={content?.modelId} show={showTestDrive} onClose={()=>setShowTestDrive(false)} />}
    </div>
  );
};
